/**
 * BillingSettings - Subscription and billing page
 *
 * Features:
 * - Current plan details
 * - Payment method management
 * - Invoice history
 * - Upgrade/downgrade options
 *
 * Design Philosophy:
 * - Clear visibility of current plan
 * - Easy access to billing history
 * - Upgrade path is visible but not pushy
 */

import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  CreditCard,
  Download,
  ExternalLink,
  CheckCircle2,
  Calendar,
  ChevronRight,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { currentCommunityAtom } from '@/lib/stores/community';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/atoms/button';
import { cn } from '@/lib/utils';

interface BillingInfo {
  plan: {
    name: string;
    price: number;
    interval: 'monthly' | 'yearly';
    memberLimit?: number;
    features: string[];
  };
  paymentMethod?: {
    type: 'card';
    brand: string;
    last4: string;
    expiryMonth: number;
    expiryYear: number;
  };
  currentPeriod: {
    start: string;
    end: string;
  };
  invoices: Array<{
    id: string;
    date: string;
    amount: number;
    status: 'paid' | 'pending' | 'failed';
    downloadUrl: string;
  }>;
}

export function BillingSettings() {
  const community = useAtomValue(currentCommunityAtom);

  // Fetch billing info
  const { data: billing, isLoading } = useQuery({
    queryKey: ['billing', community?.id],
    queryFn: async (): Promise<BillingInfo> => {
      // Would fetch from billing/subscription tables
      // For now, return mock data
      return {
        plan: {
          name: 'Pro',
          price: 49,
          interval: 'monthly',
          memberLimit: 500,
          features: [
            'Unlimited channels',
            'Custom branding',
            'Analytics dashboard',
            'Priority support',
          ],
        },
        paymentMethod: {
          type: 'card',
          brand: 'Visa',
          last4: '4242',
          expiryMonth: 12,
          expiryYear: 2025,
        },
        currentPeriod: {
          start: '2024-01-01',
          end: '2024-01-31',
        },
        invoices: [
          {
            id: 'inv_001',
            date: '2024-01-01',
            amount: 49,
            status: 'paid',
            downloadUrl: '#',
          },
          {
            id: 'inv_002',
            date: '2023-12-01',
            amount: 49,
            status: 'paid',
            downloadUrl: '#',
          },
          {
            id: 'inv_003',
            date: '2023-11-01',
            amount: 49,
            status: 'paid',
            downloadUrl: '#',
          },
        ],
      };
    },
    enabled: !!community?.id,
  });

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="animate-pulse text-[#6B6966]">Loading billing...</div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      {/* Header */}
      <header className="mb-8">
        <Link
          to="/settings/settings-v2"
          className="inline-flex items-center text-[14px] text-[#6B6966] hover:text-[#1A1A19] mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to Settings
        </Link>
        <h1 className="serif-display text-[28px] text-[#1A1A19]">Billing</h1>
        <p className="text-[15px] text-[#6B6966] mt-1">
          Manage your subscription and payment
        </p>
      </header>

      <div className="space-y-8">
        {/* Current Plan */}
        <section>
          <h2 className="text-[13px] font-medium text-[#6B6966] uppercase tracking-wide mb-4">
            Current Plan
          </h2>

          <div className="p-6 bg-white border border-[#E8E6E3] rounded-xl">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center space-x-3">
                  <h3 className="text-[24px] font-medium text-[#1A1A19]">
                    {billing?.plan.name}
                  </h3>
                  <span className="px-2 py-1 bg-[#CC785C]/10 text-[#CC785C] text-[12px] font-medium rounded-full">
                    Active
                  </span>
                </div>
                <p className="text-[15px] text-[#6B6966] mt-1">
                  ${billing?.plan.price}/
                  {billing?.plan.interval === 'monthly' ? 'month' : 'year'}
                  {billing?.plan.memberLimit &&
                    ` · Up to ${billing.plan.memberLimit} members`}
                </p>

                <div className="mt-4 space-y-2">
                  {billing?.plan.features.map((feature, i) => (
                    <div
                      key={i}
                      className="flex items-center text-[14px] text-[#6B6966]"
                    >
                      <CheckCircle2 className="w-4 h-4 text-green-500 mr-2" />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>

              <Button variant="outline" className="border-[#E8E6E3]">
                Change plan
              </Button>
            </div>

            {billing?.currentPeriod && (
              <div className="mt-6 pt-4 border-t border-[#E8E6E3]">
                <div className="flex items-center text-[13px] text-[#6B6966]">
                  <Calendar className="w-4 h-4 mr-2" />
                  Current period: {formatDate(
                    billing.currentPeriod.start
                  )} - {formatDate(billing.currentPeriod.end)}
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Payment Method */}
        <section>
          <h2 className="text-[13px] font-medium text-[#6B6966] uppercase tracking-wide mb-4">
            Payment Method
          </h2>

          <div className="p-6 bg-white border border-[#E8E6E3] rounded-xl">
            {billing?.paymentMethod ? (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-8 bg-[#F5F3F0] rounded flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-[#6B6966]" />
                  </div>
                  <div>
                    <p className="text-[14px] font-medium text-[#1A1A19]">
                      {billing.paymentMethod.brand} ••••{' '}
                      {billing.paymentMethod.last4}
                    </p>
                    <p className="text-[13px] text-[#6B6966]">
                      Expires {billing.paymentMethod.expiryMonth}/
                      {billing.paymentMethod.expiryYear}
                    </p>
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  className="border-[#E8E6E3]"
                >
                  Update
                </Button>
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-[14px] text-[#6B6966] mb-3">
                  No payment method on file
                </p>
                <Button className="bg-[#CC785C] hover:bg-[#CC785C]/90 text-white">
                  Add payment method
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* Invoices */}
        <section>
          <h2 className="text-[13px] font-medium text-[#6B6966] uppercase tracking-wide mb-4">
            Billing History
          </h2>

          <div className="bg-white border border-[#E8E6E3] rounded-xl overflow-hidden">
            {billing?.invoices && billing.invoices.length > 0 ? (
              <div className="divide-y divide-[#E8E6E3]">
                {billing.invoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className="flex items-center justify-between p-4 hover:bg-[#F5F3F0]/50 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <div>
                        <p className="text-[14px] font-medium text-[#1A1A19]">
                          {formatDate(invoice.date)}
                        </p>
                        <p className="text-[13px] text-[#6B6966]">
                          ${invoice.amount.toFixed(2)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <span
                        className={cn(
                          'px-2 py-0.5 text-[11px] font-medium rounded-full',
                          invoice.status === 'paid'
                            ? 'bg-green-100 text-green-700'
                            : invoice.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                        )}
                      >
                        {invoice.status.charAt(0).toUpperCase() +
                          invoice.status.slice(1)}
                      </span>

                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() =>
                          window.open(invoice.downloadUrl, '_blank')
                        }
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-[14px] text-[#6B6966]">No invoices yet</p>
              </div>
            )}
          </div>
        </section>

        {/* Need Help */}
        <div className="pt-6 border-t border-[#E8E6E3]">
          <p className="text-[13px] text-[#6B6966]">
            Questions about billing?{' '}
            <a
              href="mailto:support@terrarium.app"
              className="text-[#CC785C] hover:underline"
            >
              Contact support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
